import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { Subscription } from 'rxjs';
import { AppErrorCode } from 'src/app/_models/app-error-code.enum';
import { EmployeeLicenseUpgradeQuote } from '../models/employee-license';
import { BusinessServices } from '../services/business.services';

@Component({
  selector: 'employee-license-upgrade',
  standalone: true,
  imports: [CommonModule, TranslateDirective, TranslatePipe, DialogModule],
  templateUrl: './employee-license-upgrade.component.html'
})
export class EmployeeLicenseUpgradeComponent implements OnDestroy {
  @Input() businessId = 0;
  @Output() upgraded = new EventEmitter<EmployeeLicenseUpgradeQuote>();
  @Output() continued = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  visible = false;
  loading = false;
  accepting = false;
  additionalEmployees = 1;
  state: 'offer' | 'success' | 'expired' | 'restricted' | 'error' = 'offer';
  quote: EmployeeLicenseUpgradeQuote | null = null;
  private request = new Subscription();

  constructor(private readonly businesses: BusinessServices) {}

  get title(): string {
    if (this.state === 'success') return 'EMPLOYEE_LICENSE.UPGRADE_COMPLETE';
    if (this.state === 'expired' || this.state === 'restricted')
      return 'EMPLOYEE_LICENSE.LIMIT_REACHED';
    return 'EMPLOYEE_LIMIT_UPGRADE_TITLE';
  }

  open(): void {
    if (this.loading || this.accepting || this.businessId <= 0) return;
    this.additionalEmployees = 1;
    this.visible = true;
    this.loadQuote();
  }

  loadQuote(): void {
    this.request.unsubscribe();
    this.loading = true;
    this.state = 'offer';
    this.quote = null;
    this.request = this.businesses
      .getEmployeeLimitUpgradeQuote(this.businessId, this.additionalEmployees)
      .subscribe({
        next: quote => {
          this.quote = quote;
          this.additionalEmployees = quote.AdditionalEmployees;
          this.loading = false;
        },
        error: error => {
          this.loading = false;
          this.setError(error);
        }
      });
  }

  changeQuantity(change: number): void {
    if (this.loading || this.accepting || this.state !== 'offer') return;
    const quantity = this.additionalEmployees + change;
    if (!Number.isSafeInteger(quantity) || quantity < 1) return;
    this.additionalEmployees = quantity;
    this.loadQuote();
  }

  accept(): void {
    if (this.loading || this.accepting || this.state !== 'offer' || !this.quote)
      return;
    this.accepting = true;
    this.request = this.businesses
      .acceptEmployeeLimitUpgrade(this.businessId, this.quote)
      .subscribe({
        next: quote => {
          this.accepting = false;
          this.quote = quote;
          this.state = 'success';
          this.upgraded.emit(quote);
        },
        error: error => {
          this.accepting = false;
          this.setError(error);
        }
      });
  }

  continue(): void {
    this.visible = false;
    this.continued.emit();
  }

  close(): void {
    if (this.accepting) return;
    this.visible = false;
  }

  onHide(): void {
    this.request.unsubscribe();
    this.loading = false;
    if (this.state !== 'success') this.cancelled.emit();
  }

  private setError(error: {
    status?: number;
    error?: { Code?: number };
  }): void {
    if (error.error?.Code === AppErrorCode.EmployeeLicenseExpired)
      this.state = 'expired';
    else if (error.status === 403) this.state = 'restricted';
    else this.state = 'error';
  }

  ngOnDestroy(): void {
    this.request.unsubscribe();
  }
}
